/* ═══════════════════════════════════════════════════════════════
   CustomWidgetBuilder — Project Center widget builder (Builder R2,
   T4b-UI). A right-side Sheet: a live preview of the real widget body
   on top, then Name / Source / Filter / Form controls below. Source,
   filter fields and the eventual `requires` all derive from
   WIDGET_SOURCES + requiresFor (widgetSources.ts) so a saved widget
   can never read data the builder's own author lacked permission to
   see.

   Builder R2 adds two shapes (Meter, Line over time), min/max to
   Number/Bar's measure, a fixed 5/10/25 List row cap, sort direction,
   and an icon + accent picker — same live-preview contract, same
   debounce.

   The preview definition is debounced 150ms after the last edit so
   fast typing doesn't re-evaluate on every keystroke; the underlying
   source rows are fetched once per source/project and reused for both
   the live preview and the filter's live match count.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { SearchableSelect } from '../../components/SearchableSelect';
import { FilterBuilder, type FilterField } from '../../components/FilterBuilder';
import {
  EMPTY_FILTER_STATE,
  matchesFilterState,
  activeRuleCount,
} from '../../components/filterModel';
import type { TableFilterState } from '../../components/filterModel';
import { WIDGET_SOURCES } from '../../lib/widgetSources';
import { evaluateWidget, clampListMax } from '../../lib/widgetEvaluator';
import { renderEvalResult } from './widgets/custom-widget';
import { CustomWidgetService } from '../../services/customWidgetService';
import { hasPermission, hasAnyPermission } from '../../../shared/permissions';
import { toneToVar } from '../../components/widgets/WChart';
import { CUSTOM_WIDGET_ICON_OPTIONS } from '../../lib/customWidgetIcons';
import type {
  CustomWidget,
  CustomWidgetDefinition,
  CustomWidgetForm,
  CustomWidgetIcon,
  WidgetFilterState,
  WidgetMeasure,
  WidgetTone,
} from '../../../shared/entities';
import type { ProjectWidgetCtx } from './widgets/context';

export interface CustomWidgetBuilderProps {
  open: boolean;
  onClose: () => void;
  ctx: ProjectWidgetCtx;
  /** Present ⇒ editing that row; absent ⇒ creating a new one. */
  editing?: CustomWidget | null;
  surface: string;
  onSaved: (widget: CustomWidget) => void;
}

type FormKind = 'number' | 'meter' | 'list' | 'bar' | 'line';
type Measure = 'count' | 'sum' | 'avg' | 'min' | 'max';
type FieldMeasure = Exclude<Measure, 'count'>;
type ListMax = 5 | 10 | 25;

const DEFAULT_ICON: CustomWidgetIcon = 'LayoutGrid';
const DEFAULT_ACCENT: WidgetTone = 'accent';
const ACCENT_OPTIONS: WidgetTone[] = [
  'accent',
  'ok',
  'info',
  'warn',
  'danger',
  'forecast',
  'neutral',
];

interface BuilderState {
  name: string;
  sourceId: string;
  filter: TableFilterState;
  formKind: FormKind;
  // number
  numberMeasure: Measure;
  numberField: string;
  // meter
  meterMeasure: Measure;
  meterField: string;
  meterTargetKind: 'field' | 'fixed';
  meterTargetField: string;
  meterTargetOp: FieldMeasure;
  meterTargetFixed: string;
  // list
  listFields: string[];
  listSortField: string;
  listSortDir: 'asc' | 'desc';
  listMax: ListMax;
  // bar
  barGroupBy: string;
  barMeasure: Measure;
  barField: string;
  // line
  lineDateField: string;
  lineBucket: 'week' | 'month';
  lineMeasure: Measure;
  lineField: string;
  // display
  icon: CustomWidgetIcon;
  accent: WidgetTone;
  shared: boolean;
}

function emptyState(defaultSourceId: string): BuilderState {
  return {
    name: '',
    sourceId: defaultSourceId,
    filter: EMPTY_FILTER_STATE,
    formKind: 'number',
    numberMeasure: 'count',
    numberField: '',
    meterMeasure: 'count',
    meterField: '',
    meterTargetKind: 'fixed',
    meterTargetField: '',
    meterTargetOp: 'sum',
    meterTargetFixed: '100',
    listFields: [],
    listSortField: '',
    listSortDir: 'asc',
    listMax: 10,
    barGroupBy: '',
    barMeasure: 'count',
    barField: '',
    lineDateField: '',
    lineBucket: 'week',
    lineMeasure: 'count',
    lineField: '',
    icon: DEFAULT_ICON,
    accent: DEFAULT_ACCENT,
    shared: false,
  };
}

function measureToState(m: WidgetMeasure): { measure: Measure; field: string } {
  return m === 'count' ? { measure: 'count', field: '' } : { measure: m.op, field: m.field };
}

function stateFromWidget(w: CustomWidget): BuilderState {
  const def = w.definition;
  const base = emptyState(def.sourceId);
  base.name = w.name;
  base.filter = def.filter as unknown as TableFilterState;
  base.shared = !!w.shared;
  base.icon = def.icon ?? DEFAULT_ICON;
  base.accent = def.accent ?? DEFAULT_ACCENT;
  if (def.form.kind === 'number') {
    base.formKind = 'number';
    const { measure, field } = measureToState(def.form.measure);
    base.numberMeasure = measure;
    base.numberField = field;
  } else if (def.form.kind === 'meter') {
    base.formKind = 'meter';
    const { measure, field } = measureToState(def.form.measure);
    base.meterMeasure = measure;
    base.meterField = field;
    if (def.form.target.kind === 'fixed') {
      base.meterTargetKind = 'fixed';
      base.meterTargetFixed = String(def.form.target.value);
    } else {
      base.meterTargetKind = 'field';
      base.meterTargetField = def.form.target.field;
      base.meterTargetOp = def.form.target.op as BuilderState['meterTargetOp'];
    }
  } else if (def.form.kind === 'list') {
    base.formKind = 'list';
    base.listFields = def.form.fields;
    // A legacy/out-of-tier stored max (pre-dating the 5/10/25 Sheet tiers)
    // clamps UP to the nearest tier via the same rule the evaluator reads
    // with — a bare `?? 10` cast let 8/12/20 through as ListMax, which the
    // tier select can't render as a selected option.
    base.listMax = clampListMax(def.form.max) as ListMax;
    if (def.form.sort) {
      base.listSortField = def.form.sort.field;
      base.listSortDir = def.form.sort.dir;
    }
  } else if (def.form.kind === 'bar') {
    base.formKind = 'bar';
    base.barGroupBy = def.form.groupBy;
    const { measure, field } = measureToState(def.form.measure);
    base.barMeasure = measure;
    base.barField = field;
  } else {
    base.formKind = 'line';
    base.lineDateField = def.form.dateField;
    base.lineBucket = def.form.bucket;
    const { measure, field } = measureToState(def.form.measure);
    base.lineMeasure = measure;
    base.lineField = field;
  }
  return base;
}

function toWidgetMeasure(measure: Measure, field: string): WidgetMeasure {
  return measure === 'count' ? 'count' : { op: measure, field };
}

function buildDefinition(state: BuilderState): CustomWidgetDefinition {
  let form: CustomWidgetForm;
  if (state.formKind === 'number') {
    form = { kind: 'number', measure: toWidgetMeasure(state.numberMeasure, state.numberField) };
  } else if (state.formKind === 'meter') {
    form = {
      kind: 'meter',
      measure: toWidgetMeasure(state.meterMeasure, state.meterField),
      target:
        state.meterTargetKind === 'fixed'
          ? { kind: 'fixed', value: Number(state.meterTargetFixed) || 0 }
          : { kind: 'field', field: state.meterTargetField, op: state.meterTargetOp },
    };
  } else if (state.formKind === 'list') {
    form = {
      kind: 'list',
      fields: state.listFields,
      sort: state.listSortField
        ? { field: state.listSortField, dir: state.listSortDir }
        : undefined,
      max: state.listMax,
    };
  } else if (state.formKind === 'bar') {
    form = {
      kind: 'bar',
      groupBy: state.barGroupBy,
      measure: toWidgetMeasure(state.barMeasure, state.barField),
    };
  } else {
    form = {
      kind: 'line',
      dateField: state.lineDateField,
      bucket: state.lineBucket,
      measure: toWidgetMeasure(state.lineMeasure, state.lineField),
    };
  }
  return {
    version: 1,
    sourceId: state.sourceId,
    // AND rules only — the builder never offers an OR toggle, so any
    // 'any' the underlying FilterBuilder draft carried is coerced back.
    filter: { ...state.filter, match: 'all' } as unknown as WidgetFilterState,
    form,
    icon: state.icon,
    accent: state.accent,
  };
}

function resolveForFilter(
  row: Record<string, unknown>,
  field: string
): string | number | null | undefined {
  const v = row[field];
  if (v === undefined || v === null) return v as null | undefined;
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

const MEASURE_LABEL: Record<Measure, string> = {
  count: 'Count',
  sum: 'Sum of',
  avg: 'Average of',
  min: 'Min of',
  max: 'Max of',
};
const FIELD_MEASURE_LABEL: Record<FieldMeasure, string> = {
  sum: 'Sum of',
  avg: 'Average of',
  min: 'Min of',
  max: 'Max of',
};

const FORM_KIND_LABEL: Record<FormKind, string> = {
  number: 'Number',
  meter: 'Meter',
  list: 'List',
  bar: 'Bar chart',
  line: 'Line over time',
};

/** Button-pair/row toggle — the app's stand-in for a SegmentedControl
 *  (DESIGN.md §5: no dedicated primitive exists; this pattern — a row of
 *  Button variant="default"/"outline" toggles — is already how the pre-R2
 *  builder chose its measure and form kind). */
function Toggle<T extends string | number>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  labels: Record<T, string>;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <Button
          key={opt}
          type="button"
          size="sm"
          variant={value === opt ? 'default' : 'outline'}
          onClick={() => onChange(opt)}
        >
          {labels[opt]}
        </Button>
      ))}
    </div>
  );
}

/** Measure picker: Count/Sum/Average/Min/Max toggle, plus a numeric-field
 *  SearchableSelect when the measure needs one. Shared by Number/Meter/
 *  Bar/Line so the four shapes' "what to reduce" control looks identical. */
function MeasurePicker({
  measure,
  field,
  numericFieldOptions,
  onMeasureChange,
  onFieldChange,
  includeCount = true,
}: {
  measure: Measure;
  field: string;
  numericFieldOptions: { value: string; label: string }[];
  onMeasureChange: (m: Measure) => void;
  onFieldChange: (f: string) => void;
  includeCount?: boolean;
}) {
  const options = (
    includeCount ? ['count', 'sum', 'avg', 'min', 'max'] : ['sum', 'avg', 'min', 'max']
  ) as Measure[];
  return (
    <div className="flex flex-col gap-2">
      <Toggle options={options} value={measure} onChange={onMeasureChange} labels={MEASURE_LABEL} />
      {measure !== 'count' && (
        <SearchableSelect
          value={field}
          options={numericFieldOptions}
          onChange={onFieldChange}
          placeholder="Pick a number field"
        />
      )}
    </div>
  );
}

export function CustomWidgetBuilder({
  open,
  onClose,
  ctx,
  editing,
  surface,
  onSaved,
}: CustomWidgetBuilderProps) {
  const userPermissions = ctx.userPermissions;
  const ownerId = ctx.authUser?.teamMemberId || ctx.authUser?.id || '';
  const availableSources = useMemo(
    () => WIDGET_SOURCES.filter((s) => hasAnyPermission(userPermissions, s.requires)),
    [userPermissions]
  );

  const [state, setState] = useState<BuilderState>(() => emptyState(availableSources[0]?.id ?? ''));
  const [filterOpen, setFilterOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setState(editing ? stateFromWidget(editing) : emptyState(availableSources[0]?.id ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const source = availableSources.find((s) => s.id === state.sourceId) ?? availableSources[0];

  const [sourceRows, setSourceRows] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    if (!source) {
      setSourceRows([]);
      return;
    }
    let cancelled = false;
    source
      .load(ctx.project.id)
      .then((rows) => {
        if (!cancelled) setSourceRows(rows);
      })
      .catch(() => {
        if (!cancelled) setSourceRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [source, ctx.project.id]);

  // Debounce the definition fed to the live preview 150ms after the last edit.
  const [debouncedDef, setDebouncedDef] = useState<CustomWidgetDefinition | null>(null);
  useEffect(() => {
    if (!source) {
      setDebouncedDef(null);
      return;
    }
    const t = window.setTimeout(() => setDebouncedDef(buildDefinition(state)), 150);
    return () => window.clearTimeout(t);
  }, [state, source]);

  const preview =
    source && debouncedDef
      ? { result: evaluateWidget(debouncedDef, sourceRows, source), form: debouncedDef.form }
      : null;

  const canShare = hasPermission(userPermissions, 'dashboards.share_views');
  const isOwner = !!editing && editing.ownerId === ownerId;
  // Final gate expression (mirrors the server exactly — T4a's own-row +
  // share_views rules): an owner may manage with EITHER edit tier or the
  // bare per-row `dashboards.delete` key (server treats delete as own-row-
  // only); a non-owner needs the row to be shared AND `share_views` — bare
  // `dashboards.delete` alone is never enough for someone else's row.
  const ownerManage =
    isOwner &&
    (hasPermission(userPermissions, 'dashboards.edit_own') ||
      hasPermission(userPermissions, 'dashboards.edit_any') ||
      hasPermission(userPermissions, 'dashboards.delete'));
  const canDelete = !!editing && (ownerManage || (!!editing?.shared && canShare));

  const filterFields: FilterField[] = source
    ? source.fields.map((f) => ({ key: f.key, label: f.label, kind: f.kind }))
    : [];
  const numericFieldOptions = source
    ? source.fields.filter((f) => f.numeric).map((f) => ({ value: f.key, label: f.label }))
    : [];
  const anyFieldOptions = source
    ? source.fields.map((f) => ({ value: f.key, label: f.label }))
    : [];
  const dateFieldOptions = source
    ? source.fields.filter((f) => f.kind === 'date').map((f) => ({ value: f.key, label: f.label }))
    : [];
  const ruleCount = activeRuleCount(state.filter);

  function validateShape(): string | null {
    if (state.formKind === 'number' && state.numberMeasure !== 'count' && !state.numberField) {
      return 'Pick a field to sum, average, min, or max.';
    }
    if (state.formKind === 'meter') {
      if (state.meterMeasure !== 'count' && !state.meterField) {
        return 'Pick a field for the meter to measure.';
      }
      if (state.meterTargetKind === 'field' && !state.meterTargetField) {
        return 'Pick a target field.';
      }
      if (state.meterTargetKind === 'fixed' && !state.meterTargetFixed.trim()) {
        return 'Enter a target number.';
      }
    }
    if (state.formKind === 'list' && state.listFields.length === 0) {
      return 'Pick at least one field to show.';
    }
    if (
      state.formKind === 'bar' &&
      (!state.barGroupBy || (state.barMeasure !== 'count' && !state.barField))
    ) {
      return 'Pick a group-by field (and a measure field).';
    }
    if (state.formKind === 'line') {
      if (!state.lineDateField) return 'Pick a date field.';
      if (state.lineMeasure !== 'count' && !state.lineField) {
        return 'Pick a field to measure.';
      }
    }
    return null;
  }

  async function handleSave() {
    if (!source) return;
    if (!state.name.trim()) {
      setError('Name is required.');
      return;
    }
    const shapeError = validateShape();
    if (shapeError) {
      setError(shapeError);
      return;
    }
    const definition = buildDefinition(state);
    const row: CustomWidget = {
      id: editing?.id ?? '',
      ownerId: editing?.ownerId ?? ownerId,
      surface,
      name: state.name.trim(),
      definition,
      shared: canShare ? state.shared : false,
      createdAt: editing?.createdAt,
    };
    setSaving(true);
    setError(null);
    try {
      const saved = await CustomWidgetService.save(row);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || 'Could not save this widget.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editing) return;
    setDeleting(true);
    try {
      await CustomWidgetService.delete(editing.id);
      setConfirmDeleteOpen(false);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not delete this widget.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && !saving && onClose()}>
        <SheetContent
          side="right"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
        >
          <SheetHeader>
            <SheetTitle>{editing ? 'Edit widget' : 'New widget'}</SheetTitle>
            <SheetDescription>Build a widget from your project&apos;s own data.</SheetDescription>
          </SheetHeader>

          <div
            style={{
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3)',
              background: 'var(--sf)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.06em',
                marginBottom: 'var(--space-2)',
              }}
            >
              Preview
            </div>
            {preview ? (
              // Static on purpose — a live preview never drills/navigates;
              // only accent is applied so it matches the real card's tint.
              renderEvalResult(preview.result, preview.form, { accent: state.accent })
            ) : (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                {availableSources.length === 0
                  ? 'No data sources available to you yet.'
                  : 'Pick a source to preview.'}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cwb-name">Name</Label>
              <Input
                id="cwb-name"
                autoFocus
                value={state.name}
                onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Open deliverables"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Source</Label>
              <SearchableSelect
                value={state.sourceId}
                options={availableSources.map((s) => ({ value: s.id, label: s.label }))}
                onChange={(v) =>
                  setState((s) => ({
                    ...s,
                    sourceId: v,
                    numberField: '',
                    meterField: '',
                    meterTargetField: '',
                    listFields: [],
                    listSortField: '',
                    barGroupBy: '',
                    barField: '',
                    lineDateField: '',
                    lineField: '',
                    filter: EMPTY_FILTER_STATE,
                  }))
                }
                placeholder="Select a data source"
                clearable={false}
                disabled={availableSources.length === 0}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Filter</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterOpen(true)}
                disabled={!source}
              >
                {ruleCount > 0
                  ? `${ruleCount} rule${ruleCount === 1 ? '' : 's'}`
                  : 'No filter — all rows'}
              </Button>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Icon</Label>
              <SearchableSelect
                value={state.icon}
                options={CUSTOM_WIDGET_ICON_OPTIONS}
                onChange={(v) => setState((s) => ({ ...s, icon: v as CustomWidgetIcon }))}
                placeholder="Pick an icon"
                clearable={false}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Accent</Label>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {ACCENT_OPTIONS.map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    aria-label={`Accent: ${tone}`}
                    title={tone}
                    onClick={() => setState((s) => ({ ...s, accent: tone }))}
                    style={{
                      width: 'var(--space-6)',
                      height: 'var(--space-6)',
                      borderRadius: 'var(--radius-full)',
                      background: toneToVar(tone),
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow:
                        state.accent === tone
                          ? '0 0 0 2px var(--sf), 0 0 0 4px var(--ac)'
                          : '0 0 0 1px var(--bd)',
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Form</Label>
              <Toggle
                options={['number', 'meter', 'list', 'bar', 'line'] as FormKind[]}
                value={state.formKind}
                onChange={(k) => setState((s) => ({ ...s, formKind: k }))}
                labels={FORM_KIND_LABEL}
              />
            </div>

            {state.formKind === 'number' && (
              <MeasurePicker
                measure={state.numberMeasure}
                field={state.numberField}
                numericFieldOptions={numericFieldOptions}
                onMeasureChange={(m) => setState((s) => ({ ...s, numberMeasure: m }))}
                onFieldChange={(f) => setState((s) => ({ ...s, numberField: f }))}
              />
            )}

            {state.formKind === 'meter' && (
              <div className="flex flex-col gap-3">
                <Label>Measures</Label>
                <MeasurePicker
                  measure={state.meterMeasure}
                  field={state.meterField}
                  numericFieldOptions={numericFieldOptions}
                  onMeasureChange={(m) => setState((s) => ({ ...s, meterMeasure: m }))}
                  onFieldChange={(f) => setState((s) => ({ ...s, meterField: f }))}
                />
                <Label>Target</Label>
                <Toggle
                  options={['fixed', 'field'] as const}
                  value={state.meterTargetKind}
                  onChange={(k) => setState((s) => ({ ...s, meterTargetKind: k }))}
                  labels={{ fixed: 'Fixed number', field: 'Field' }}
                />
                {state.meterTargetKind === 'fixed' ? (
                  <Input
                    type="number"
                    value={state.meterTargetFixed}
                    onChange={(e) => setState((s) => ({ ...s, meterTargetFixed: e.target.value }))}
                    placeholder="e.g. 100"
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    <Toggle
                      options={['sum', 'avg', 'min', 'max'] as FieldMeasure[]}
                      value={state.meterTargetOp}
                      onChange={(op) => setState((s) => ({ ...s, meterTargetOp: op }))}
                      labels={FIELD_MEASURE_LABEL}
                    />
                    <SearchableSelect
                      value={state.meterTargetField}
                      options={numericFieldOptions}
                      onChange={(v) => setState((s) => ({ ...s, meterTargetField: v }))}
                      placeholder="Pick a number field"
                    />
                  </div>
                )}
              </div>
            )}

            {state.formKind === 'list' && (
              <div className="flex flex-col gap-2">
                <Label>Fields to show</Label>
                <SearchableSelect
                  value=""
                  onChange={() => {}}
                  multiple
                  values={state.listFields}
                  onChangeMulti={(vals) => setState((s) => ({ ...s, listFields: vals }))}
                  options={anyFieldOptions}
                  placeholder="Pick fields"
                />
                <Label>Sort by</Label>
                <SearchableSelect
                  value={state.listSortField}
                  options={anyFieldOptions}
                  onChange={(v) => setState((s) => ({ ...s, listSortField: v }))}
                  placeholder="No sort"
                />
                {state.listSortField && (
                  <Toggle
                    options={['asc', 'desc'] as const}
                    value={state.listSortDir}
                    onChange={(d) => setState((s) => ({ ...s, listSortDir: d }))}
                    labels={{ asc: 'Ascending', desc: 'Descending' }}
                  />
                )}
                <Label>Row cap</Label>
                <Toggle
                  options={[5, 10, 25] as ListMax[]}
                  value={state.listMax}
                  onChange={(m) => setState((s) => ({ ...s, listMax: m }))}
                  labels={{ 5: '5', 10: '10', 25: '25' } as Record<ListMax, string>}
                />
              </div>
            )}

            {state.formKind === 'bar' && (
              <div className="flex flex-col gap-2">
                <Label>Group by</Label>
                <SearchableSelect
                  value={state.barGroupBy}
                  options={anyFieldOptions}
                  onChange={(v) => setState((s) => ({ ...s, barGroupBy: v }))}
                  placeholder="Pick a field"
                />
                <Label>Measure</Label>
                <MeasurePicker
                  measure={state.barMeasure}
                  field={state.barField}
                  numericFieldOptions={numericFieldOptions}
                  onMeasureChange={(m) => setState((s) => ({ ...s, barMeasure: m }))}
                  onFieldChange={(f) => setState((s) => ({ ...s, barField: f }))}
                />
              </div>
            )}

            {state.formKind === 'line' && (
              <div className="flex flex-col gap-2">
                <Label>Date field</Label>
                <SearchableSelect
                  value={state.lineDateField}
                  options={dateFieldOptions}
                  onChange={(v) => setState((s) => ({ ...s, lineDateField: v }))}
                  placeholder="Pick a date field"
                  noResultsText="No date fields on this source"
                />
                <Label>Bucket</Label>
                <Toggle
                  options={['week', 'month'] as const}
                  value={state.lineBucket}
                  onChange={(b) => setState((s) => ({ ...s, lineBucket: b }))}
                  labels={{ week: 'Week', month: 'Month' }}
                />
                <Label>Measure</Label>
                <MeasurePicker
                  measure={state.lineMeasure}
                  field={state.lineField}
                  numericFieldOptions={numericFieldOptions}
                  onMeasureChange={(m) => setState((s) => ({ ...s, lineMeasure: m }))}
                  onFieldChange={(f) => setState((s) => ({ ...s, lineField: f }))}
                />
              </div>
            )}

            {canShare && (
              <label className="flex items-center gap-2 text-sm text-muted">
                <Switch
                  checked={state.shared}
                  onCheckedChange={(v) => setState((s) => ({ ...s, shared: v }))}
                />
                Share with the firm
              </label>
            )}

            {error && (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--dg)' }}>{error}</div>
            )}
          </div>

          <SheetFooter
            style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between' }}
          >
            {canDelete ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDeleteOpen(true)}
                style={{ color: 'var(--dg)' }}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !source}>
                {saving ? 'Saving…' : 'Save for me'}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <FilterBuilder
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fields={filterFields}
        value={state.filter}
        totalRows={sourceRows.length}
        countMatches={(draft) =>
          sourceRows.filter((row) =>
            matchesFilterState(draft, (field) => resolveForFilter(row, field))
          ).length
        }
        onApply={(next) => {
          setState((s) => ({ ...s, filter: { ...next, match: 'all' } }));
          setFilterOpen(false);
        }}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete widget?"
        description={`"${state.name || editing?.name || 'This widget'}" will be removed from every dashboard it's shared to.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDelete}
      />
    </>
  );
}
